import React, { useState, useEffect } from "react";
import NameInput from "./NameInput";
import ContactInput from "./ContactInput";
import ContactListInput from "./ContactListInput";
import AddressInput from "./AddressInput";
import FloorMapInput from "./FloorMapInput";
import GuidelinesInput from "./GuidelinesInput";
import { Phone, Trash, UserCircle } from "@phosphor-icons/react";
import axiosInstance from "../../../api/axiosInstance";
import PageLoader from "../../loader/PageLoader";
import ImageLoader from "../../loader/ImageLoader";
import {toast} from "react-toastify";
import LandingInput from "./LandingInput";
import ProfileInput from "./ProfileInput";
import WelcomeMessageInput from "./WelcomeMessageInput";
import WifiInput from "./WifiInput";
import TimingsInput from "./TimingsInput";
import GoogleReviewInput from "./GoogleReviewInput";
import FreeParkingInput from "./FreeParkingInput";
import PhotoPreview from "./PhotoPreview";
import { uploadImageToServer } from "../../../api/uploadImage";

const AboutHotel = ({ onComplete }) => {

    const [profilePhoto, setProfilePhoto] = useState({
        key: "",
        url: "",
        loading: false,
        error: false,
    });

    const [landingPhoto, setLandingPhoto] = useState({
        key: "",
        url: "",
        loading: false,
        error: false,
    });

    const [welcomePhoto, setWelcomePhoto] = useState({
        key: "",
        url: "",
        loading: false,
        error: false,
    });

    const [formData, setFormData] = useState({
        name: "",
        contact: null,
        address: "",
        city: "",
        state: "",
        postal_code: "",
        floor_map: [],
        guidelines: [],
        countryCode : "91",

        // New fields
        landing_photo: "",
        profile_photo: "",
        manager_contacts: [],
        food_and_beverages_manager_contacts: [],
        housekeeping_manager_contacts: [],

        welcome_message_photo: "",
        wifi_name: "",
        wifi_password: "",
        google_review_link: "",
        kitchen_timings: "",
        pool_timings: "",
        service_start_time: "",
        service_end_time: "",
        checkin_time: "",
        checkout_time: "",
        has_free_parking: false,
    });
    const [isExistingUser, setIsExistingUser] = useState(false);
    const [isPageLoading, setIsPageLoading] = useState(true);
    const [media, setMedia] = useState([]);
    const [errors, setErrors] = useState({});
    const [currentGuidelineInput, setCurrentGuidelineInput] = useState("");

    const hotelId = localStorage.getItem("hotelId");

    // For Postal Code
    const [buttonDisabled,setButtonDisabled] = useState(false);

    // Single-image fields and the state they render into
    const singleImageSetters = {
        profile_photo: setProfilePhoto,
        landing_photo: setLandingPhoto,
        welcome_message_photo: setWelcomePhoto,
    };

    const uploadSingleImage = async (input, field) => {
        // Normalize input into a single File
        let file = input;

        // If FileList
        if (input instanceof FileList) {
            file = input[0];
        }

        // If array
        if (Array.isArray(input)) {
            file = input[0];
        }

        // If input event
        if (input?.target?.files) {
            file = input.target.files[0];
        }

        // Make sure we actually have a File
        if (!(file instanceof File)) {
            console.error("Invalid file received:", input);
            return;
        }

        const previewUrl = URL.createObjectURL(file);

        const preview = {
            key: file.name,
            url: previewUrl,
            loading: true,
            error: false,
        };

        const setPhoto = singleImageSetters[field];
        setPhoto(preview);

        try {
            const uploadUrlResponse = await axiosInstance.get(
                `/api/v1/images/upload-presigned-url?file_name=${encodeURIComponent(
                    file.name
                )}`
            );

            const { key, url } = uploadUrlResponse.data;

            const success = await uploadImageToServer(file, url);

            if (!success) {
                throw new Error("Image upload failed");
            }

            const downloadResponse = await axiosInstance.get(
                `/api/v1/images/download-presigned-url?key=${encodeURIComponent(
                    key
                )}`
            );

            setPhoto({
                key,
                url: downloadResponse.data.url,
                loading: false,
                error: false,
            });

            setFormData((prev) => ({
                ...prev,
                [field]: key,
            }));

            // Remove temporary browser preview
            URL.revokeObjectURL(previewUrl);
        } catch (error) {
            console.error(`Error uploading ${field}:`, error);

            setPhoto({
                key: "",
                url: "",
                loading: false,
                error: true,
            });

            URL.revokeObjectURL(previewUrl);
        }
    };


    useEffect(() => {
        if (hotelId) {
            fetchHotelData();
        }
    }, [hotelId]);

    const splitPhoneNumber = (phoneNumber) => {
        if (!phoneNumber) {
            return { countryCode: "91", contact: "" };
        }

        const numberStr = phoneNumber.toString();

        // Numbers stored without a country code prefix are local (India)
        if (numberStr.length <= 10) {
            return { countryCode: "91", contact: numberStr };
        }

        // Anything longer carries a prefix; the last 10 digits are the number
        return {
            countryCode: numberStr.substring(0, numberStr.length - 10),
            contact: numberStr.substring(numberStr.length - 10),
        };
    };

    // Manager contacts arrive as a list of plain numbers and are edited as
    // country-code/number pairs, one row per number.
    const toContactRows = (values) =>
        (values || []).map((value) => {
            const { countryCode, contact } = splitPhoneNumber(value);
            return { country_code: countryCode, contact };
        });

    const toContactNumbers = (rows) =>
        (rows || [])
            .filter((row) => row.contact?.trim())
            .map((row) => Number(`${row.country_code}${row.contact.trim()}`));

    const fetchHotelData = async () => {
        try {
            const response = await axiosInstance.get(`/api/v1/hotels/${hotelId}`);
            const { data } = response;

            const hasInitialData =
                data.name ||
                data.contact ||
                data.address ||
                data.city ||
                data.state ||
                data.postal_code ||
                data.profile_photo ||
                data.landing_photo ||
                data.manager_contacts?.length ||
                data.food_and_beverages_manager_contacts?.length ||
                data.housekeeping_manager_contacts?.length ||
                data.welcome_message_photo ||
                data.wifi_name ||
                data.google_review_link ||
                data.kitchen_timings ||
                data.pool_timings;

            if (!hasInitialData) {
                setIsExistingUser(false);
                setIsPageLoading(false);
                return;
            }

            // Normalize phone numbers independently
            const {
                countryCode,
                contact,
            } = splitPhoneNumber(data.contact || "");


            // Set form data
            setFormData({
                ...data,

                // Hotel contact
                contact: contact || "",
                countryCode: countryCode || "91",

                // Manager contacts
                manager_contacts: toContactRows(data.manager_contacts),

                // Food & Beverage manager contacts
                food_and_beverages_manager_contacts: toContactRows(
                    data.food_and_beverages_manager_contacts
                ),

                // Housekeeping manager contacts
                housekeeping_manager_contacts: toContactRows(
                    data.housekeeping_manager_contacts
                ),

                // Other fields
                guidelines: data.guidelines || [],
                floor_map: data.floor_map || [],
                welcome_message_photo: data.welcome_message_photo || "",
                wifi_name: data.wifi_name || "",
                wifi_password: data.wifi_password || "",
                google_review_link: data.google_review_link || "",
                kitchen_timings: data.kitchen_timings || "",
                pool_timings: data.pool_timings || "",
                service_start_time: data.service_start_time || "",
                service_end_time: data.service_end_time || "",
                checkin_time: data.checkin_time || "",
                checkout_time: data.checkout_time || "",
                has_free_parking: data.has_free_parking || false,
            });

            // Fetch the single-image photo URLs in parallel
            const photoRequests = Object.entries(singleImageSetters)
                .filter(([field]) => data[field])
                .map(([field, setPhoto]) =>
                    axiosInstance
                        .get(
                            `/api/v1/images/download-presigned-url?key=${data[field]}`
                        )
                        .then((response) => {
                            setPhoto({
                                key: data[field],
                                url: response.data.url,
                                loading: false,
                                error: false,
                            });
                        })
                        .catch((error) => {
                            console.error(
                                `Error fetching ${field}:`,
                                error
                            );

                            setPhoto((prev) => ({
                                ...prev,
                                loading: false,
                                error: true,
                            }));
                        })
                );

            await Promise.all(photoRequests);

            // Floor plan images
            const existingPhotos = data.floor_map || [];

            setMedia(
                existingPhotos.map((key) => ({
                    key,
                    url: "",
                    loading: true,
                    error: false,
                }))
            );

            if (existingPhotos.length > 0) {
                await fetchFloorPlanImages(existingPhotos);
            }

            setIsExistingUser(true);
            setIsPageLoading(false);
        } catch (error) {
            console.error("Error fetching hotel data:", error);
            setIsExistingUser(false);
            setIsPageLoading(false);
        }
    };

    const handleCountryCodeChange = (codeField, contactField, code) => {
        setFormData((prevData) => ({
            ...prevData,
            [codeField]: code,
        }));
        setErrors((prevErrors) => ({
            ...prevErrors,
            [contactField]: "",
        }));
    };

    const handleInputChange = (field, value) => {
        setFormData((prevData) => ({
            ...prevData,
            [field]:
                field === "contact"
                    ? String(value)
                    : field === "guidelines"
                    ? value
                    : value,
        }));
        setErrors((prevErrors) => ({
            ...prevErrors,
            [field]: "",
        }));
    };
    const handleGuidelineInputChange = (value) => {
        setCurrentGuidelineInput(value);
    };

    const fetchFloorPlanImages = async (imageKeys) => {
        setMedia(
            imageKeys.map((key) => ({
                key,
                url: "",
                loading: true,
                error: false,
            })),
        );

        const imagePromises = imageKeys.map(async (key) => {
            try {
                const response = await axiosInstance.get(
                    `/api/v1/images/download-presigned-url?key=${key}`,
                );
                const imageUrl = response.data.url;
                return { key, url: imageUrl, loading: false, error: false };
            } catch (error) {
                console.error("Error fetching image URL:", error);
                return { key, url: "", loading: false, error: true };
            }
        });

        const images = await Promise.all(imagePromises);
        setMedia(images);
    };

    const handleFileUpload = async (files) => {
        // console.log("Initial files:", files);

        // Ensure we're working with File objects
        const fileArray = Array.from(files).filter(
            (file) => file instanceof File,
        );

        // console.log("Filtered fileArray:", fileArray);

        if (fileArray.length === 0) {
            console.warn("No valid files found.");
            return;
        }

        const newMediaEntries = fileArray.map((file) => {
            // console.log("Creating object URL for file:", file.name);
            return {
                key: file.name,
                url: URL.createObjectURL(file),
                loading: true,
                error: false,
            };
        });

        // console.log("New media entries:", newMediaEntries);

        setMedia((prevMedia) => [...prevMedia, ...newMediaEntries]);
        // Clear the floor_map error when new files are uploaded
        setErrors((prevErrors) => ({
            ...prevErrors,
            floor_map: "",
        }));
        // Update formData.floor_map
        setFormData((prevData) => ({
            ...prevData,
            floor_map: [
                ...prevData.floor_map,
                ...newMediaEntries.map((entry) => entry.key),
            ],
        }));
        const uploadPromises = newMediaEntries.map(async (entry, index) => {
            try {
                // console.log(`Fetching upload URL for ${entry.key}`);
                const uploadUrlResponse = await axiosInstance.get(
                    `/api/v1/images/upload-presigned-url?file_name=${entry.key}`,
                );

                // console.log("Upload URL response:", uploadUrlResponse.data);

                const { key, url } = uploadUrlResponse.data;
                // console.log(`Uploading ${fileArray[index].name} to ${url}`);
                const isSuccess = await uploadImageToServer(
                    fileArray[index],
                    url,
                );

                setMedia((prevMedia) =>
                    prevMedia.map((mediaEntry) =>
                        mediaEntry.key === entry.key
                            ? {
                                  ...mediaEntry,
                                  key: key,
                                  loading: false,
                                  error: !isSuccess,
                              }
                            : mediaEntry,
                    ),
                );

                // console.log(
                //     `Upload ${isSuccess ? "successful" : "failed"} for ${
                //         entry.key
                //     }`,
                // );
                return isSuccess;
            } catch (error) {
                console.error(`Error uploading ${entry.key}:`, error);
                setMedia((prevMedia) =>
                    prevMedia.map((mediaEntry) =>
                        mediaEntry.key === entry.key
                            ? { ...mediaEntry, loading: false, error: true }
                            : mediaEntry,
                    ),
                );
                return false;
            }
        });

        await Promise.all(uploadPromises);
    };

    const removeFloorPlan = (indexToRemove) => {
        setMedia((prevMedia) =>
            prevMedia.filter((_, index) => index !== indexToRemove),
        );
        setFormData((prevData) => ({
            ...prevData,
            floor_map: prevData.floor_map.filter(
                (_, index) => index !== indexToRemove,
            ),
        }));

        // // If all floor plans are removed, set the error again
        // if (media.length === 1) {
        //     setErrors((prevErrors) => ({
        //         ...prevErrors,
        //         floor_map: "At least one floor plan is required",
        //     }));
        // }
    };

    const [isSubmitting, setIsSubmitting] = useState(false);
    const addGuideline = () => {
        if (currentGuidelineInput.trim()) {
            setFormData((prevData) => ({
                ...prevData,
                guidelines: [
                    ...prevData.guidelines,
                    currentGuidelineInput.trim(),
                ],
            }));
            setCurrentGuidelineInput("");
        }
    };

    const handleSubmit = async () => {
        setIsPageLoading(true);
        if (isSubmitting) return; // Prevent multiple submissions
        // console.log("FormData for About Hotel : ",formData);
        let updatedGuidelines = [...formData.guidelines];
        if (currentGuidelineInput.trim()) {
            updatedGuidelines = [
                ...updatedGuidelines,
                currentGuidelineInput.trim(),
            ];
        }

        // Update formData with the latest guidelines
        const updatedFormData = {
            ...formData,
            guidelines: updatedGuidelines,
        };
        addGuideline();
        if (!validateForm()) {
            // console.log("Form has errors. Please correct them.");
            setIsPageLoading(false);
            return;
        }

        setIsSubmitting(true);
        // console.log("Submitting form with data:", formData);
        const allImageKeys = media
            .filter((image) => !image.loading && !image.error)
            .map((image) => image.key);

            
        const updatedData = {
            ...updatedFormData,

            contact: Number(`${formData.countryCode}${formData.contact}`),
            postal_code: Number(formData.postal_code),

            floor_map: allImageKeys,
            profile_photo: formData.profile_photo,
            landing_photo: formData.landing_photo,

            manager_contacts: toContactNumbers(formData.manager_contacts),

            food_and_beverages_manager_contacts: toContactNumbers(
                formData.food_and_beverages_manager_contacts
            ),

            housekeeping_manager_contacts: toContactNumbers(
                formData.housekeeping_manager_contacts
            ),

            welcome_message_photo: formData.welcome_message_photo,
            wifi_name: formData.wifi_name.trim(),
            wifi_password: formData.wifi_password.trim(),
            google_review_link: formData.google_review_link.trim(),
            kitchen_timings: formData.kitchen_timings.trim(),
            pool_timings: formData.pool_timings.trim(),
            service_start_time: formData.service_start_time,
            service_end_time: formData.service_end_time,
            checkin_time: formData.checkin_time,
            checkout_time: formData.checkout_time,
            has_free_parking: Boolean(formData.has_free_parking),
        };
 
        try {
            if (isExistingUser) {
                // console.log("Updating existing hotel data...");
                await axiosInstance.patch(
                    `/api/v1/hotels/${hotelId}/general-info`,
                    updatedData,
                );
                // console.log("Update successful");
            } else {
                // console.log("Submitting new hotel data...");
                // console.log("Form Data : ",updatedData);
                if(updatedData.guidelines.length===0){
                    updatedData.guidelines = null;
                }
                // console.log("Hotel Id ",hotelId);
                await axiosInstance.post(
                    `/api/v1/hotels/${hotelId}/general-info`,
                    updatedData,
                );
                // console.log("Submission successful");
            }
            // Call onComplete to signal completion

            onComplete();
        } catch (error) {
            toast.error(error.response.data.message)
            console.error("Error submitting data:", error);
            // Optional: Add user feedback or error messages
        } finally {
            setIsSubmitting(false);
            setIsPageLoading(false);
        }
    };

    const validateForm = () => {
        const newErrors = {};
        if (!formData.name.trim()) newErrors.name = "Name is required";
        if (!formData.contact) newErrors.contact = "Contact is required";
        if (formData.contact && formData.contact.length < 10) {
            newErrors.contact = "Please enter 10digit contact";
        }
        // Manager contacts are optional, but every number typed into a row must
        // be 10 digits; errors are per row so the right one gets flagged.
        const optionalContacts = [
            ["manager_contacts", "General Manager"],
            ["food_and_beverages_manager_contacts", "Food and Beverages Manager"],
            ["housekeeping_manager_contacts", "Housekeeping Manager"],
        ];
        optionalContacts.forEach(([field, label]) => {
            const rowErrors = (formData[field] || []).map((row) => {
                const value = row.contact?.trim();
                if (value && value.length !== 10) {
                    return `Please enter 10digit ${label} contact`;
                }
                return "";
            });
            if (rowErrors.some(Boolean)) {
                newErrors[field] = rowErrors;
            }
        });

        if (!formData.address.trim()) newErrors.address = "Address is required";
        if (!formData.city.trim()) newErrors.city = "City is required";
        if (!formData.state.trim()) newErrors.state = "State is required";
        if (!formData.postal_code)
            newErrors.postal_code = "Postal code is required";

        // Optional, but must be a usable URL when provided
        const reviewLink = formData.google_review_link.trim();
        if (reviewLink && !/^https?:\/\/\S+$/i.test(reviewLink)) {
            newErrors.google_review_link =
                "Enter a valid link starting with http:// or https://";
        }

        if (formData.postal_code.toString().length !== 6) {
            // console.log("postal code", formData.postal_code.toString.length);
            newErrors.postal_code = "Please enter 6digit postal_code";
        }
        // if (formData.floor_map.length === 0)
        //     newErrors.floor_map = "At least one floor plan is required";
        // if (formData.guidelines.length === 0 && !currentGuidelineInput.trim())
        //     newErrors.guidelines = "Guidelines are required";

        // One-sided hours would read as a window the guest can never satisfy,
        // so either both are set or neither is.
        if (
            Boolean(formData.service_start_time) !==
            Boolean(formData.service_end_time)
        ) {
            newErrors.service_hours =
                "Enter both a start and an end time, or leave both blank";
        }

        if (
            Boolean(formData.checkin_time) !== Boolean(formData.checkout_time)
        ) {
            newErrors.stay_hours =
                "Enter both a check-in and a checkout time, or leave both blank";
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };
    const handleRemoveGuideline = (guidelineIndex, lineIndex) => {
        setFormData((prevData) => {
            // Remove the specific line from the guideline
            const updatedGuidelines = prevData.guidelines
                .map((guideline, index) => {
                    if (index === guidelineIndex) {
                        // Split the guideline into lines, remove the specific line, then join back
                        return guideline
                            .split(",")
                            .filter((_, i) => i !== lineIndex)
                            .join(",");
                    }
                    return guideline;
                })
                .filter((guideline) => guideline.trim() !== ""); // Optional: Remove empty guidelines

            return {
                ...prevData,
                guidelines: updatedGuidelines,
            };
        });
    };

    return (
        <>
            {isPageLoading && <PageLoader />}
            <div className="about-hotel flex">
                {/* left form */}
                <div className="w-1/2 p-4 pr-0 border-r-[1.5px]">
                    <NameInput
                        value={formData.name}
                        onChange={(value) => handleInputChange("name", value)}
                        error={errors.name}
                    />
                    <ContactInput
                        value={formData.contact}
                        countryCode={formData.countryCode}
                        onCountryCodeChange={(code) =>
                            handleCountryCodeChange(
                                "countryCode",
                                "contact",
                                code
                            )
                        }
                        onChange={(value) =>
                            handleInputChange("contact", value)
                        }
                        error={errors.contact}
                        name = "Hotel Contact"
                    />
                    <ContactListInput
                        name="General Manager"
                        rows={formData.manager_contacts}
                        onChange={(rows) =>
                            handleInputChange("manager_contacts", rows)
                        }
                        errors={errors.manager_contacts}
                    />
                    <ContactListInput
                        name="Food and Beverages Manager"
                        rows={formData.food_and_beverages_manager_contacts}
                        onChange={(rows) =>
                            handleInputChange(
                                "food_and_beverages_manager_contacts",
                                rows
                            )
                        }
                        errors={errors.food_and_beverages_manager_contacts}
                    />

                    <ContactListInput
                        name="Housekeeping Manager"
                        rows={formData.housekeeping_manager_contacts}
                        onChange={(rows) =>
                            handleInputChange(
                                "housekeeping_manager_contacts",
                                rows
                            )
                        }
                        errors={errors.housekeeping_manager_contacts}
                    />

                    <AddressInput
                        address={formData.address}
                        postal_code={formData.postal_code}
                        city={formData.city}
                        state={formData.state}
                        setButtonDisabled = {setButtonDisabled}
                        onChange={(field, value) =>
                            handleInputChange(field, value)
                        }
                        errors={{
                            address: errors.address,
                            postal_code: errors.postal_code,
                            city: errors.city,
                            state: errors.state,
                        }}
                    />{" "}
                    <FloorMapInput
                        value={formData.floor_map}
                        onChange={handleFileUpload}
                        error={errors.floor_map}
                    />
                    <GuidelinesInput
                        value={formData.guidelines}
                        onChange={(value) =>
                            handleInputChange("guidelines", value)
                        }
                        error={errors.guidelines}
                        onInputChange={handleGuidelineInputChange}
                    />

                    <ProfileInput
                        error={
                            profilePhoto.error
                                ? "Profile photo upload failed. Please try again."
                                : ""
                        }
                        loading={profilePhoto.loading}
                        onChange={(file) => uploadSingleImage(file, "profile_photo")}
                    />

                    <LandingInput
                        error={
                            landingPhoto.error
                                ? "Landing photo upload failed. Please try again."
                                : ""
                        }
                        loading={landingPhoto.loading}
                        onChange={(file) => uploadSingleImage(file, "landing_photo")}
                    />

                    <WelcomeMessageInput
                        error={
                            welcomePhoto.error
                                ? "Welcome message photo upload failed. Please try again."
                                : ""
                        }
                        loading={welcomePhoto.loading}
                        onChange={(file) =>
                            uploadSingleImage(file, "welcome_message_photo")
                        }
                    />

                    <WifiInput
                        wifi_name={formData.wifi_name}
                        wifi_password={formData.wifi_password}
                        onChange={(field, value) =>
                            handleInputChange(field, value)
                        }
                        errors={{
                            wifi_name: errors.wifi_name,
                            wifi_password: errors.wifi_password,
                        }}
                    />

                    <TimingsInput
                        kitchen_timings={formData.kitchen_timings}
                        pool_timings={formData.pool_timings}
                        service_start_time={formData.service_start_time}
                        service_end_time={formData.service_end_time}
                        checkin_time={formData.checkin_time}
                        checkout_time={formData.checkout_time}
                        onChange={(field, value) =>
                            handleInputChange(field, value)
                        }
                        errors={{
                            kitchen_timings: errors.kitchen_timings,
                            pool_timings: errors.pool_timings,
                            service_hours: errors.service_hours,
                            stay_hours: errors.stay_hours,
                        }}
                    />

                    <GoogleReviewInput
                        value={formData.google_review_link}
                        onChange={(value) =>
                            handleInputChange("google_review_link", value)
                        }
                        error={errors.google_review_link}
                    />

                    <FreeParkingInput
                        value={formData.has_free_parking}
                        onChange={(value) =>
                            handleInputChange("has_free_parking", value)
                        }
                    />

                    {/* Submit Button */}
                    <button
                        className={`mt-6 px-4 py-2 text-white rounded-md absolute bottom-3 left-[19%] ${buttonDisabled ? 'bg-[#A4B8FF] cursor-not-allowed opacity-50' : 'bg-[#0B22B2]'}`}
                        onClick={handleSubmit}
                        disabled = {buttonDisabled}
                    >
                        Next
                    </button>

                </div>
                {/* right content */}
                <div className="w-1/2 py-4">

                    {/* Name */}
                    <div className="h-[112px]">
                        {formData.name && (
                            <div className="bg-[#F2F7FF] p-3 h-full border-b-[1.5px]">
                                <p className="text-[#737880]">Added Name</p>
                                <div className="w-fit flex items-center justify-between border-[1.5px] border-[#BFC4CC] rounded-lg p-0 mt-1">
                                    <div className="flex items-center gap-2 border-r-[1.3px] border-[#BFC4CC] h-full text-[#737880] px-3 py-2">
                                        <UserCircle
                                            className="text-xl"
                                            weight="bold"
                                        />
                                        <span>
                                           
                                            <span className="ps-1">
                                                {formData.name}
                                            </span>
                                        </span>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>


                    {/* Contact */}
                    <div className="h-[146px]">
                        {formData.contact && (
                            <div className="bg-[#F2F7FF] p-3 h-full border-b-[1.5px]">
                                <p className="text-[#737880]">Added Hotel Contact</p>
                                <div className="w-fit flex items-center justify-between border-[1.5px] border-[#BFC4CC] rounded-lg p-0 mt-1">
                                    <div className="flex items-center gap-2 border-r-[1.3px] border-[#BFC4CC] h-full text-[#737880] px-3 py-2">
                                        <Phone
                                            className="text-xl"
                                            weight="bold"
                                        />
                                        <span>
                                            +{formData.countryCode}
                                            <span className="ps-1">
                                                {formData.contact}
                                            </span>
                                        </span>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Manager contacts */}
                    {[
                        ["manager_contacts", "General Manager Contact"],
                        [
                            "food_and_beverages_manager_contacts",
                            "Food & Beverages Contact",
                        ],
                        [
                            "housekeeping_manager_contacts",
                            "Housekeeping Contact",
                        ],
                    ].map(([field, title]) => {
                        const rows = (formData[field] || []).filter(
                            (row) => row.contact?.trim()
                        );

                        return (
                            <div key={field} className="min-h-[146px]">
                                {rows.length > 0 && (
                                    <div className="bg-[#F2F7FF] p-3 h-full border-b-[1.5px]">
                                        <p className="text-[#737880]">
                                            Added {title}
                                        </p>
                                        <div className="flex flex-col gap-2 mt-1">
                                            {rows.map((row, index) => (
                                                <div
                                                    key={index}
                                                    className="w-fit flex items-center justify-between border-[1.5px] border-[#BFC4CC] rounded-lg p-0"
                                                >
                                                    <div className="flex items-center gap-2 border-r-[1.3px] border-[#BFC4CC] h-full text-[#737880] px-3 py-2">
                                                        <Phone
                                                            className="text-xl"
                                                            weight="bold"
                                                        />
                                                        <span>
                                                            +{row.country_code}
                                                            <span className="ps-1">
                                                                {row.contact}
                                                            </span>
                                                        </span>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        );
                    })}


                    {/* Address */}
                    <div className="h-[260px]">
                        {formData.address && formData.address.length > 0 && (
                            <div className="h-full bg-[#F2F7FF] border-b-[1.5px] p-3">
                                <p className="text-[#737880]">Added Address</p>
                                <div className="border-[1.5px] p-2 rounded-lg w-[95%] mt-1">
                                    {formData.address} <br />
                                    {formData.city} <br />
                                    {formData.state} <br />
                                    {formData.postal_code} <br />
                                </div>
                            </div>
                        )}{" "}
                    </div>{" "}

                    {/* Floor plans */}
                    <div className="h-[200px]">
                        {media.length > 0 && (
                            <div className="h-full border-b-[1.5px] bg-[#f2f7ff] p-3">
                                <p className="text-[#737880]">
                                    Uploaded Floor Plan
                                </p>
                                <div className="flex gap-2 w-full flex-nowrap mt-1 overflow-x-auto">
                                    {media.map((file, index) => (
                                        <div
                                            key={index}
                                            className="w-[158px] h-[142px] rounded-lg border-[1.5px] border-[#BFC4CC] overflow-hidden flex-shrink-0"
                                        >
                                            {file.loading ? (
                                                <ImageLoader
                                                    width="100%"
                                                    height="110px"
                                                />
                                            ) : (
                                                <>
                                                    <img
                                                        src={file.url}
                                                        alt={`Floor Plan ${
                                                            index + 1
                                                        }`}
                                                        className="w-full bg-contain h-[110px] object-contain"
                                                        onLoad={() =>
                                                            setMedia(
                                                                (prevMedia) =>
                                                                    prevMedia.map(
                                                                        (
                                                                            mediaEntry,
                                                                        ) =>
                                                                            mediaEntry.key ===
                                                                            file.key
                                                                                ? {
                                                                                      ...mediaEntry,
                                                                                      loading: false,
                                                                                      error: false,
                                                                                  }
                                                                                : mediaEntry,
                                                                    ),
                                                            )
                                                        }
                                                        onError={() =>
                                                            setMedia(
                                                                (prevMedia) =>
                                                                    prevMedia.map(
                                                                        (
                                                                            mediaEntry,
                                                                        ) =>
                                                                            mediaEntry.key ===
                                                                            file.key
                                                                                ? {
                                                                                      ...mediaEntry,
                                                                                      loading: false,
                                                                                      error: true,
                                                                                  }
                                                                                : mediaEntry,
                                                                    ),
                                                            )
                                                        }
                                                    />
                                                    <div className="flex justify-between items-center h-[32px] px-1 bg-[#EEF0FB]">
                                                        <p className="text-xs">
                                                            Floor Plan{" "}
                                                            {index + 1}
                                                        </p>
                                                        <button
                                                            className="bg-white rounded-md p-1 border-[1.5px] border-[#F8D9DE] text-[#DC4662] h-[24px] w-[24px]"
                                                            onClick={() =>
                                                                removeFloorPlan(
                                                                    index,
                                                                )
                                                            }
                                                        >
                                                            <Trash size={14} />
                                                        </button>
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>


                    {/* Guidelines */}
                    {formData.guidelines.length > 0 && (
                        <div className="h-[200px] p-3 bg-[#f2f7ff] overflow-y-auto border-b-[1.5px]">
                            <p className="text-[#737880]">Added Guidelines</p>
                            <ul className="list-disc pl-5 mt-1">
                                {formData.guidelines.flatMap(
                                    (guideline, index) =>
                                        guideline
                                            .split(",")
                                            .map((line, lineIndex) => (
                                                <div
                                                    key={`${index}-${lineIndex}`}
                                                    className="flex items-center gap-3"
                                                >
                                                    <li className="w-fit">
                                                        {line.trim()}
                                                    </li>
                                                    <button
                                                        onClick={() =>
                                                            handleRemoveGuideline(
                                                                index,
                                                                lineIndex,
                                                            )
                                                        }
                                                        className="bg-white rounded-md p-1 border-[1.5px] border-[#F8D9DE] text-[#DC4662] h-[24px] w-[24px]"
                                                    >
                                                        <Trash />
                                                    </button>
                                                </div>
                                            )),
                                )}
                            </ul>
                        </div>
                    )}
                    

                    {/* Profile Photo */}
                    <PhotoPreview
                        label="Profile Photo"
                        photo={profilePhoto}
                        alt="Hotel Profile"
                        emptyText="No profile photo"
                    />

                    {/* Landing Photo */}
                    <PhotoPreview
                        label="Landing Photo"
                        photo={landingPhoto}
                        alt="Hotel Landing"
                        emptyText="No landing photo"
                    />

                    {/* Welcome Message Photo */}
                    <PhotoPreview
                        label="Welcome Message Photo"
                        photo={welcomePhoto}
                        alt="Hotel Welcome Message"
                        emptyText="No welcome message photo"
                    />

                    {/* Wifi */}
                    <div className="h-[180px]">
                        {(formData.wifi_name || formData.wifi_password) && (
                            <div className="bg-[#F2F7FF] p-3 h-full border-b-[1.5px]">
                                <p className="text-[#737880]">Added Wifi</p>
                                <div className="border-[1.5px] p-2 rounded-lg w-[95%] mt-1">
                                    <p>
                                        <span className="text-[#737880]">
                                            Name:{" "}
                                        </span>
                                        {formData.wifi_name || "—"}
                                    </p>
                                    <p>
                                        <span className="text-[#737880]">
                                            Password:{" "}
                                        </span>
                                        {formData.wifi_password || "—"}
                                    </p>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Timings */}
                    <div className="h-[180px]">
                        {(formData.kitchen_timings ||
                            formData.pool_timings) && (
                            <div className="bg-[#F2F7FF] p-3 h-full border-b-[1.5px]">
                                <p className="text-[#737880]">Added Timings</p>
                                <div className="border-[1.5px] p-2 rounded-lg w-[95%] mt-1">
                                    <p>
                                        <span className="text-[#737880]">
                                            Kitchen:{" "}
                                        </span>
                                        {formData.kitchen_timings || "—"}
                                    </p>
                                    <p>
                                        <span className="text-[#737880]">
                                            Pool:{" "}
                                        </span>
                                        {formData.pool_timings || "—"}
                                    </p>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Google Review Link */}
                    <div className="h-[112px]">
                        {formData.google_review_link && (
                            <div className="bg-[#F2F7FF] p-3 h-full border-b-[1.5px]">
                                <p className="text-[#737880]">
                                    Added Google Review Link
                                </p>
                                <div className="border-[1.5px] p-2 rounded-lg w-[95%] mt-1 truncate">
                                    {formData.google_review_link}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Free Parking */}
                    <div className="h-[90px]">
                        <div className="bg-[#F2F7FF] p-3 h-full border-b-[1.5px]">
                            <p className="text-[#737880]">Free Parking</p>
                            <div className="w-fit border-[1.5px] border-[#BFC4CC] rounded-lg px-3 py-2 mt-1 text-[#737880]">
                                {formData.has_free_parking
                                    ? "Available"
                                    : "Not available"}
                            </div>
                        </div>
                    </div>

                </div>
            </div>
        </>
    );
};

export default AboutHotel;
